// CV / GCP / IATA belgeleri için ortak tanımlar.
//
// Belgeler `CVDocuments` koleksiyonunda tutulur; her kayıt bir `employeeId` ve
// bir `documentType` taşır. Çalışan belgelerini kendi profilinden
// (Profil › CV/GCP/IATA), yönetici ise Çalışan Yönetimi'nden görür.

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/**
 * Belge türleri ve zorunluluk durumu.
 * `required: true` olanlar eksikse çalışan girişte uyarı alır.
 * İngilizce CV herkesten istenmediği için zorunlu değil — değiştirmek için
 * aşağıdaki `required` alanını güncellemek yeterli.
 */
export const CV_DOCUMENT_TYPES = [
  { key: 'CV-TR', label: 'Türkçe CV', profileTab: 'cv-tr', required: true },
  { key: 'CV-EN', label: 'İngilizce CV', profileTab: 'cv-en', required: false },
  { key: 'GCP', label: 'GCP', profileTab: 'gcp', required: true },
  { key: 'IATA', label: 'IATA', profileTab: 'iata', required: true },
];

export const REQUIRED_CV_DOCUMENT_TYPES = CV_DOCUMENT_TYPES.filter((t) => t.required);

/** Bir çalışanın tüm CV/GCP/IATA belgeleri, yeni yüklenen önce. */
export const fetchEmployeeCvDocuments = async (employeeId) => {
  if (!employeeId) return [];

  const snapshot = await getDocs(
    query(collection(db, 'CVDocuments'), where('employeeId', '==', employeeId))
  );
  const documents = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Firestore tarafında orderBy kullanılmıyor: createdAt alanı olmayan eski
  // kayıtlar sorgudan düşerdi. Sıralama istemcide yapılır.
  documents.sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() || new Date(a.uploadedAt || 0);
    const dateB = b.createdAt?.toDate?.() || new Date(b.uploadedAt || 0);
    return dateB - dateA;
  });

  return documents;
};

/** Belgeleri türe göre gruplar; boş gruplar listeye girmez. */
export const groupCvDocuments = (documents = []) =>
  CV_DOCUMENT_TYPES.map((type) => ({
    ...type,
    docs: documents.filter((doc) => doc.documentType === type.key),
  })).filter((group) => group.docs.length > 0);

/** Henüz hiç yüklenmemiş zorunlu belge türleri. */
export const getMissingRequiredDocuments = (documents = []) => {
  const uploadedTypes = new Set(documents.map((doc) => doc.documentType));
  return REQUIRED_CV_DOCUMENT_TYPES.filter((type) => !uploadedTypes.has(type.key));
};

/**
 * Birden fazla çalışanın eksik zorunlu belgeleri: `{ [employeeId]: [tür, ...] }`.
 * Ekip listeleri için çalışan başına tek tek sorgu atmak yerine kullanılır;
 * Firestore `in` sorgusu en fazla 30 değer aldığından sorgu parçalara bölünür.
 */
export const fetchMissingRequiredDocumentsByEmployee = async (employeeIds = []) => {
  const ids = [...new Set(employeeIds.filter(Boolean))];
  if (ids.length === 0) return {};

  const uploadedTypes = {};
  ids.forEach((id) => {
    uploadedTypes[id] = new Set();
  });

  const BATCH_SIZE = 30;
  const batches = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    batches.push(ids.slice(i, i + BATCH_SIZE));
  }

  const snapshots = await Promise.all(
    batches.map((batch) =>
      getDocs(query(collection(db, 'CVDocuments'), where('employeeId', 'in', batch)))
    )
  );

  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((d) => {
      const { employeeId, documentType } = d.data();
      if (uploadedTypes[employeeId]) uploadedTypes[employeeId].add(documentType);
    });
  });

  const missingByEmployee = {};
  ids.forEach((id) => {
    missingByEmployee[id] = REQUIRED_CV_DOCUMENT_TYPES.filter(
      (type) => !uploadedTypes[id].has(type.key)
    );
  });
  return missingByEmployee;
};
